import Vue from 'vue'
import Router from 'vue-router'
import multiguard from 'vue-router-multiguard'
import authGuard from './core/middleware/auth-guard'
import authSignedGuard from './core/middleware/auth-signed-guard'
import permissionsGuard from './core/middleware/permissions-guard'
import Login from './pages/login.vue'
import Logout from './pages/logout.vue'
import Home from './pages/index.vue'
import Trainees from './pages/trainees/index.vue'
import TraineeShow from './pages/trainees/[id].vue'
import Workouts from './pages/workouts/index.vue'
import WorkoutShow from './pages/workouts/[id].vue'
import WorkoutView from './pages/workouts/view.vue'
import WorkoutExecutions from './pages/workout-executions/index.vue'
import WorkoutExecutionView from './pages/workout-executions/view.vue'
import Feedback from './pages/feedback/index.vue'
import Programs from './pages/programs/index.vue'
import ProgramShow from './pages/programs/[id].vue'
import ProgramView from './pages/programs/view.vue'
import ProgramExecutions from './pages/program-executions/index.vue'
import Races from './pages/races/index.vue'
import HealthSections from './pages/health/sections.vue'
import HealthSectionShow from './pages/health/section.[id].vue'
import HealthSectionView from './pages/health/section.view.vue'
import HealthArticles from './pages/health/articles.vue'
import HealthArticleShow from './pages/health/article.[id].vue'
import HealthArticleView from './pages/health/article.view.vue'
import GlossarySections from './pages/glossary/sections.vue'
import GlossarySectionShow from './pages/glossary/section.[id].vue'
import GlossarySectionView from './pages/glossary/section.view.vue'
import GlossaryDefinitions from './pages/glossary/definitions.vue'
import GlossaryDefinitionShow from './pages/glossary/definition.[id].vue'
import GlossaryDefinitionView from './pages/glossary/definition.view.vue'
import AdviceSections from './pages/advices/sections.vue'
import AdviceSectionShow from './pages/advices/section.[id].vue'
import AdviceSectionView from './pages/advices/section.view.vue'
import Advices from './pages/advices/advices.vue'
import AdviceShow from './pages/advices/advice.[id].vue'
import AdviceView from './pages/advices/advice.view.vue'
import TagSections from './pages/tags/sections.vue'
import TagSectionShow from './pages/tags/section.[id].vue'
import TagSectionView from './pages/tags/section.view.vue'
import Tags from './pages/tags/tags.vue'
import TagShow from './pages/tags/tag.[id].vue'
import TagView from './pages/tags/tag.view.vue'
import ExternalContents from './pages/external-contents/index.vue'
import ExternalContentShow from './pages/external-contents/[id].vue'
import ExternalContentView from './pages/external-contents/view.vue'
import BadgeSections from './pages/badges/sections.vue'
import BadgeSectionShow from './pages/badges/section.[id].vue'
import BadgeSectionView from './pages/badges/section.view.vue'
import Badges from './pages/badges/badges.vue'
import BadgeShow from './pages/badges/badge.[id].vue'
import BadgeView from './pages/badges/badge.view.vue'
import Events from './pages/events/index.vue'
import EventShow from './pages/events/[id].vue'
import EventView from './pages/events/view.vue'
import Groups from './pages/groups/index.vue'
import GroupShow from './pages/groups/[id].vue'
import GroupView from './pages/groups/view.vue'
import Email from './pages/email/index.vue'
import Statistic from './pages/statistic/index.vue'
import UserStatistic from './pages/statistic/user-statistic.vue'
import Notifications from './pages/notifications/index.vue'
import NotificationShow from './pages/notifications/[id].vue'
import NotificationView from './pages/notifications/view.vue'
import AppContents from './pages/app-contents/index.vue'
import Complaints from './pages/complaints/index.vue'
import ComplaintView from './pages/complaints/view.vue'
import Translations from './pages/translations/index.vue'
import Users from './pages/users/users.vue'
import Roles from './pages/users/roles.vue'
import RoleShow from './pages/users/role.[id].vue'
import Permissions from './pages/users/permissions.vue'
import Params from './pages/params/index.vue'
import Pages from './pages/pages/index.vue'
import PageShow from './pages/pages/[id].vue'
import PageNotFound from './pages/[...all].vue'
import PageForbidden from './pages/403.vue'
import PremiumPersonalizedPlans from './pages/premium/personalized-plans.vue'
import PremiumOneMonthTrial from './pages/premium/one-month-trial.vue'

Vue.use(Router)

const routes = [
    {
        name: 'login',
        path: '/login',
        component: Login,
        beforeEnter: multiguard([authSignedGuard]),
        meta: {
            layout: 'auth'
        }
    },
    {
        name: 'logout',
        path: '/logout',
        component: Logout,
        beforeEnter: multiguard([authGuard])
    },
    {
        name: 'home',
        path: '/',
        component: Home,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_dashboard']
        }
    },
    {
        name: 'trainees',
        path: '/trainees',
        component: Trainees,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_trainees']
        }
    },
    {
        name: 'trainees.show',
        path: '/trainees/:id',
        component: TraineeShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_trainees']
        }
    },
    {
        name: 'workouts',
        path: '/workouts',
        component: Workouts,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_workouts']
        }
    },
    {
        name: 'workout-executions',
        path: '/workouts/workout-executions',
        component: WorkoutExecutions,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_workout_executions']
        }
    },
    {
        name: 'workout-executions.view',
        path: '/workouts/workout-executions/:id/view',
        component: WorkoutExecutionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_workout_executions']
        }
    },
    {
        name: 'feedback',
        path: '/workouts/feedback',
        component: Feedback,
        beforeEnter: multiguard([authGuard]),
    },
    {
        name: 'workouts.create',
        path: '/workouts/create',
        component: WorkoutShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_workouts']
        }
    },
    {
        name: 'workouts.show',
        path: '/workouts/:id',
        component: WorkoutShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_workouts']
        }
    },
    {
        name: 'workouts.view',
        path: '/workouts/:id/view',
        component: WorkoutView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_workouts']
        }
    },
    {
        name: 'programs',
        path: '/programs',
        component: Programs,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_programs']
        }
    },
    {
        name: 'program-executions',
        path: '/programs/program-executions',
        component: ProgramExecutions,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_program_executions']
        }
    },
    {
        name: 'programs.create',
        path: '/programs/create',
        component: ProgramShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_programs']
        }
    },
    {
        name: 'programs.show',
        path: '/programs/:id',
        component: ProgramShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_programs']
        }
    },
    {
        name: 'programs.view',
        path: '/programs/:id/view',
        component: ProgramView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_programs']
        }
    },
    {
        name: 'races',
        path: '/races',
        component: Races,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_races']
        }
    },
    {
        name: 'badge-sections',
        path: '/badges',
        component: BadgeSections,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badge-sections.create',
        path: '/badges/sections/create',
        component: BadgeSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badge-sections.show',
        path: '/badges/sections/:id',
        component: BadgeSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badge-sections.view',
        path: '/badges/sections/:id/view',
        component: BadgeSectionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badges',
        path: '/badges/badges',
        component: Badges,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badges.create',
        path: '/badges/badges/create',
        component: BadgeShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badges.show',
        path: '/badges/badges/:id',
        component: BadgeShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'badges.view',
        path: '/badges/badges/:id/view',
        component: BadgeView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_badges']
        }
    },
    {
        name: 'events',
        path: '/events',
        component: Events,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_events']
        }
    },
    {
        name: 'events.create',
        path: '/events/create',
        component: EventShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_events']
        }
    },
    {
        name: 'events.show',
        path: '/events/:id',
        component: EventShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_events']
        }
    },
    {
        name: 'events.view',
        path: '/events/:id/view',
        component: EventView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_events']
        }
    },
    {
        name: 'groups',
        path: '/groups',
        component: Groups,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_groups']
        }
    },
    {
        name: 'groups.create',
        path: '/groups/create',
        component: GroupShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_groups']
        }
    },
    {
        name: 'groups.show',
        path: '/groups/:id',
        component: GroupShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_groups']
        }
    },
    {
        name: 'groups.view',
        path: '/groups/:id/view',
        component: GroupView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_groups']
        }
    },
    {
        name: 'email',
        path: '/email',
        component: Email,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_email']
        }
    },
    {
        name: 'statistic',
        path: '/statistic',
        component: Statistic,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_statistic']
        }
    },
    {
        name: 'user-statistic',
        path: '/statistic/user-statistic',
        component: UserStatistic,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_statistic']
        }
    },
    {
        name: 'notifications',
        path: '/notifications',
        component: Notifications,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_notifications']
        }
    },
    {
        name: 'notifications.create',
        path: '/notifications/create',
        component: NotificationShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_notifications']
        }
    },
    {
        name: 'notifications.show',
        path: '/notifications/:id',
        component: NotificationShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_notifications']
        }
    },
    {
        name: 'notifications.view',
        path: '/notifications/:id/view',
        component: NotificationView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_notifications']
        }
    },
    {
        name: 'app-contents',
        path: '/app-contents',
        component: AppContents,
        beforeEnter: multiguard([authGuard])
    },
    {
        name: 'advice-sections',
        path: '/app-contents/advices',
        component: AdviceSections,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advice-sections.create',
        path: '/app-contents/advices/sections/create',
        component: AdviceSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advice-sections.show',
        path: '/app-contents/advices/sections/:id',
        component: AdviceSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advice-sections.view',
        path: '/app-contents/advices/sections/:id/view',
        component: AdviceSectionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advices',
        path: '/app-contents/advices/advices',
        component: Advices,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advices.create',
        path: '/app-contents/advices/advices/create',
        component: AdviceShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advices.show',
        path: '/app-contents/advices/advices/:id',
        component: AdviceShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'advices.view',
        path: '/app-contents/advices/advices/:id/view',
        component: AdviceView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_advices']
        }
    },
    {
        name: 'glossary-sections',
        path: '/app-contents/glossary',
        component: GlossarySections,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-sections.create',
        path: '/app-contents/glossary/sections/create',
        component: GlossarySectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-sections.show',
        path: '/glossary/sections/:id',
        component: GlossarySectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-sections.view',
        path: '/app-contents/glossary/sections/:id/view',
        component: GlossarySectionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-definitions',
        path: '/app-contents/glossary/definitions',
        component: GlossaryDefinitions,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-definitions.create',
        path: '/app-contents/glossary/definitions/create',
        component: GlossaryDefinitionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-definitions.show',
        path: '/app-contents/glossary/definitions/:id',
        component: GlossaryDefinitionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'glossary-definitions.view',
        path: '/app-contents/glossary/definitions/:id/view',
        component: GlossaryDefinitionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_glossary']
        }
    },
    {
        name: 'sections',
        path: '/app-contents/health',
        component: HealthSections,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'sections.create',
        path: '/app-contents/health/sections/create',
        component: HealthSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'sections.show',
        path: '/app-contents/health/sections/:id',
        component: HealthSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'sections.view',
        path: '/app-contents/health/sections/:id/view',
        component: HealthSectionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'articles',
        path: '/app-contents/health/articles',
        component: HealthArticles,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'articles.create',
        path: '/app-contents/health/articles/create',
        component: HealthArticleShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'articles.show',
        path: '/app-contents/health/articles/:id',
        component: HealthArticleShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'articles.view',
        path: '/app-contents/health/articles/:id/view',
        component: HealthArticleView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_health']
        }
    },
    {
        name: 'external-contents',
        path: '/app-contents/external-contents',
        component: ExternalContents,
        beforeEnter: multiguard([authGuard])
    },
    {
        name: 'external-contents.create',
        path: '/app-contents/external-contents/create',
        component: ExternalContentShow,
        beforeEnter: multiguard([authGuard])
    },
    {
        name: 'external-contents.show',
        path: '/app-contents/external-contents/:id',
        component: ExternalContentShow,
        beforeEnter: multiguard([authGuard])
    },
    {
        name: 'external-contents.view',
        path: '/app-contents/external-contents/:id/view',
        component: ExternalContentView,
        beforeEnter: multiguard([authGuard])
    },
    {
        name: 'tag-sections',
        path: '/app-contents/tags',
        component: TagSections,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tag-sections.create',
        path: '/app-contents/tags/sections/create',
        component: TagSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tag-sections.show',
        path: '/app-contents/tags/sections/:id',
        component: TagSectionShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tag-sections.view',
        path: '/app-contents/tags/sections/:id/view',
        component: TagSectionView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tags',
        path: '/app-contents/tags/tags',
        component: Tags,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tags.create',
        path: '/app-contents/tags/tags/create',
        component: TagShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tags.show',
        path: '/app-contents/tags/tags/:id',
        component: TagShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'tags.view',
        path: '/app-contents/tags/tags/:id/view',
        component: TagView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_tags']
        }
    },
    {
        name: 'translations',
        path: '/translations',
        component: Translations,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_translations']
        }
    },
    {
        name: 'complaints',
        path: '/complaints',
        component: Complaints,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_complaints']
        }
    },
    {
        name: 'complaints.view',
        path: '/complaints/:id/view',
        component: ComplaintView,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_complaints']
        }
    },
    {
        name: 'users',
        path: '/users',
        component: Users,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_users']
        }
    },
    {
        name: 'roles',
        path: '/users/roles',
        component: Roles,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_users']
        }
    },
    {
        name: 'roles.create',
        path: '/users/roles/create',
        component: RoleShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['*']
        }
    },
    {
        name: 'roles.show',
        path: '/users/roles/:id',
        component: RoleShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['*']
        }
    },
    {
        name: 'permissions',
        path: '/users/permissions',
        component: Permissions,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_users']
        }
    },
    {
        name: 'pages',
        path: '/pages',
        component: Pages,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_pages']
        }
    },
    {
        name: 'pages.show',
        path: '/pages/:id',
        component: PageShow,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_pages']
        }
    },
    {
        name: 'params',
        path: '/params',
        component: Params,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_params']
        }
    },
    {
        name: 'premium.personalized-plans',
        path: '/premium/personalized-plans',
        component: PremiumPersonalizedPlans,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_premium']
        }
    },
    {
        name: 'premium.one-month-trial',
        path: '/premium/one-month-trial',
        component: PremiumOneMonthTrial,
        beforeEnter: multiguard([authGuard]),
        meta: {
            permissions: ['admin_premium']
        }
    },
    {
        name: '403',
        path: '/page-403',
        component: PageForbidden,
        beforeEnter: multiguard([authGuard])
    },
    {
        path: '*',
        component: PageNotFound,
        meta: {
            layout: 'empty'
        }
    }
]

const createRouter = () => new Router({
    mode: 'history',
    routes
})

const router = createRouter()

router.beforeEach(multiguard([permissionsGuard]))

export default router
